import { Mutation } from '@apollo/client/react/components';
import { CKButton } from '@clearkit/react';
import CircularProgress from '@material-ui/core/CircularProgress';

import CheckmarkIcon from '~/images/onboarding/icon-checkmark.svg';
import WarningIcon from '~/images/onboarding/icon-warning.svg';
import { configurations } from '~/lib/configuration';
import {
  trackOnboardingCompleted,
  trackOnboardingIntegrationTested,
} from '~/lib/segmentAnalytics';

import mutation from './mutation.graphql';
import styles from './styles';

const testMessages = {
  success: 'Receiving data',
  error: 'Not receiving data',
};

const getButtonText = (service) => {
  switch (service) {
    case configurations.google:
      return 'Verify With Google Tag Manager';
    case configurations.segment:
      return 'Verify With Segment';
    default:
      return 'Test integration';
  }
};

const getIntegrationType = (service) => {
  switch (service) {
    case configurations.google:
      return 'Google Tag Manager';
    case configurations.segment:
      return 'Segment';
    default:
      return 'clearbit.js';
  }
};

const TestIntegrationButton = ({
  classes,
  test,
  callback,
  navigate,
  className,
  service,
}) => {
  const [testMessage, setTestMessage] = useState('');
  const [testing, setTesting] = useState(false);

  const testIntegration = async () => {
    setTesting(true);
    setTestMessage('');

    const query = await test();
    const { firstPageEventReceivedAt } = query.data.account.node.accountSetting;

    if (firstPageEventReceivedAt) {
      setTestMessage(testMessages.success);
      callback();
    } else {
      setTestMessage(testMessages.error);
    }

    trackOnboardingIntegrationTested(
      getIntegrationType(service),
      !!firstPageEventReceivedAt,
    );

    setTesting(false);
  };

  let testMessageClass;
  let messageAdornment;

  if (testMessage === testMessages.success) {
    testMessageClass = classes.green;
    messageAdornment = <CheckmarkIcon />;
  } else {
    testMessageClass = classes.red;
    messageAdornment = <WarningIcon className={classes.statusIcon} />;
  }

  return (
    <div className={className}>
      <span className={classnames(classes.testMessage, testMessageClass)}>
        {testMessage !== '' && messageAdornment}
        {testMessage}
      </span>
      {testMessage === testMessages.success ? (
        <CKButton
          variantColor="green"
          variant="bold"
          onClick={() => {
            trackOnboardingCompleted();
            navigate();
          }}>
          Start using Clearbit
        </CKButton>
      ) : (
        <CKButton
          variant="bold"
          variantColor="blue"
          classes={{ root: classes.testButtonRoot }}
          onClick={testIntegration}>
          {testing && (
            <CircularProgress className={classes.circularProgress} size={16} />
          )}
          {getButtonText(service)}
        </CKButton>
      )}
    </div>
  );
};

TestIntegrationButton.defaultProps = {
  callback: () => {},
  navigate: () => {},
  leftAligned: false,
};

TestIntegrationButton.propTypes = {
  classes: PropTypes.object.isRequired,
  test: PropTypes.func.isRequired,
  callback: PropTypes.func,
  navigate: PropTypes.func,
  className: PropTypes.string,
  service: PropTypes.string,
};

const WrappedTestIntegrationButton = (props) => {
  return (
    <Mutation mutation={mutation}>
      {(refreshAccountPageEvent) => (
        <TestIntegrationButton test={refreshAccountPageEvent} {...props} />
      )}
    </Mutation>
  );
};

export default withStyles(styles)(WrappedTestIntegrationButton);
